import type { FeedbackInfo, SubmitFeedbackRequest } from '@compass/client/types/feedback';
import { FeedbackType } from '@compass/client/types/feedback';
import { feedbackService } from '@services/api';
import { queryKeys } from '@services/query.config';
import { useStore } from '@store/index';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useMemo } from 'react';
import { toast } from 'sonner';

import { QC_EVAL_DETAIL_QUERY_CONFIG } from '@/constants/queryConfig';

const getFeedbackTimestamp = (feedback: FeedbackInfo) => {
  const rawTimestamp = feedback.created_at || feedback.updated_at;
  if (!rawTimestamp) {
    return 0;
  }

  const parsedTimestamp = Date.parse(rawTimestamp);
  return Number.isNaN(parsedTimestamp) ? 0 : parsedTimestamp;
};

const compareFeedbackNewestFirst = (left: FeedbackInfo, right: FeedbackInfo) => {
  const timestampDiff = getFeedbackTimestamp(right) - getFeedbackTimestamp(left);
  if (timestampDiff !== 0) {
    return timestampDiff;
  }

  return (right.id || '').localeCompare(left.id || '');
};

export const buildEvaluationFeedbackCollections = (feedback: FeedbackInfo[] | undefined) => {
  const evaluationFeedbacks: FeedbackInfo[] = [];
  const feedbacksByFindingIndex = new Map<number, FeedbackInfo[]>();

  for (const item of feedback ?? []) {
    if (item.feedback_type === FeedbackType.FEEDBACK_TYPE_EVALUATION) {
      evaluationFeedbacks.push(item);
      continue;
    }

    if (
      item.feedback_type === FeedbackType.FEEDBACK_TYPE_EVALUATION_FINDING &&
      item.finding_index !== undefined &&
      item.finding_index !== null
    ) {
      const existing = feedbacksByFindingIndex.get(item.finding_index) ?? [];
      existing.push(item);
      feedbacksByFindingIndex.set(item.finding_index, existing);
    }
  }

  evaluationFeedbacks.sort(compareFeedbackNewestFirst);

  for (const [findingIndex, findingFeedback] of feedbacksByFindingIndex.entries()) {
    feedbacksByFindingIndex.set(
      findingIndex,
      [...findingFeedback].sort(compareFeedbackNewestFirst),
    );
  }

  return { evaluationFeedbacks, feedbacksByFindingIndex };
};

/**
 * Fetches evaluation-scoped feedback and exposes evaluation-level
 * feedback plus grouped finding feedback lists.
 */
export const useEvaluationFeedback = (evaluationId: string | null | undefined) => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);

  const query = useQuery({
    queryKey: queryKeys.feedback.byEvaluation(evaluationId || ''),
    queryFn: async () => {
      const feedback: FeedbackInfo[] = [];
      let cursor: string | undefined;
      const seenCursors = new Set<string>();

      for (;;) {
        const response = await feedbackService.listFeedback({
          organization_id: selectedOrganizationId!,
          evaluation_ids: [evaluationId!],
          limit: 100,
          start_after_id: cursor,
        });
        feedback.push(...(response.feedback || []));

        const nextCursor = response.next_cursor || undefined;
        if (!nextCursor || seenCursors.has(nextCursor)) {
          break;
        }

        seenCursors.add(nextCursor);
        cursor = nextCursor;
      }

      return feedback;
    },
    enabled: !!selectedOrganizationId && !!evaluationId,
    ...QC_EVAL_DETAIL_QUERY_CONFIG,
  });

  const { evaluationFeedbacks, feedbacksByFindingIndex } = useMemo(
    () => buildEvaluationFeedbackCollections(query.data),
    [query.data],
  );

  return { ...query, evaluationFeedbacks, feedbacksByFindingIndex };
};

const normalizeEvaluationIds = (evaluationIds: string[] | null | undefined) =>
  [...new Set((evaluationIds ?? []).filter(Boolean))].sort();

const FEEDBACK_STATUS_QUERY_CONFIG = {
  staleTime: 5 * 60 * 1000,
  refetchOnWindowFocus: false,
  // QC list status can otherwise stay stale after a detail page shows newer feedback.
  refetchOnMount: 'always',
  retry: 2,
} as const;

export type EvaluationFeedbackStatusState = boolean | undefined;

export const buildEvaluationFeedbackStatusMap = (
  requestedEvaluationIds: string[],
  feedback: FeedbackInfo[] | undefined,
  defaultValue: EvaluationFeedbackStatusState = undefined,
) => {
  const statusMap = new Map<string, EvaluationFeedbackStatusState>();

  for (const evaluationId of requestedEvaluationIds) {
    statusMap.set(evaluationId, defaultValue);
  }

  for (const item of feedback ?? []) {
    if (
      !item.evaluation_id ||
      (item.feedback_type !== FeedbackType.FEEDBACK_TYPE_EVALUATION &&
        item.feedback_type !== FeedbackType.FEEDBACK_TYPE_EVALUATION_FINDING)
    ) {
      continue;
    }
    statusMap.set(item.evaluation_id, true);
  }

  return statusMap;
};

export const useEvaluationFeedbackStatuses = (
  evaluationIds: string[] | null | undefined,
  organizationIdOverride?: string | null,
) => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const effectiveOrganizationId = organizationIdOverride || selectedOrganizationId;
  const normalizedEvaluationIds = useMemo(
    () => normalizeEvaluationIds(evaluationIds),
    [evaluationIds],
  );

  const query = useQuery({
    queryKey: queryKeys.feedback.byEvaluations(
      effectiveOrganizationId || '',
      normalizedEvaluationIds,
    ),
    queryFn: async () => {
      const feedback: FeedbackInfo[] = [];
      let cursor: string | undefined;
      const seenCursors = new Set<string>();

      for (;;) {
        const response = await feedbackService.listFeedback({
          organization_id: effectiveOrganizationId!,
          evaluation_ids: normalizedEvaluationIds,
          limit: 100,
          start_after_id: cursor,
        });
        feedback.push(...(response.feedback || []));

        const nextCursor = response.next_cursor || undefined;
        if (!nextCursor || seenCursors.has(nextCursor)) {
          break;
        }

        seenCursors.add(nextCursor);
        cursor = nextCursor;
      }

      return feedback;
    },
    enabled: !!effectiveOrganizationId && normalizedEvaluationIds.length > 0,
    ...FEEDBACK_STATUS_QUERY_CONFIG,
  });

  const feedbackStatusByEvaluationId = useMemo(
    () =>
      buildEvaluationFeedbackStatusMap(
        normalizedEvaluationIds,
        query.data,
        query.isSuccess ? false : undefined,
      ),
    [normalizedEvaluationIds, query.data, query.isSuccess],
  );

  return { ...query, feedbackStatusByEvaluationId };
};

interface SubmitEvaluationFeedbackParams {
  evaluationId: string;
  text: string;
  findingIndex?: number;
}

export const useSubmitEvaluationFeedback = () => {
  const queryClient = useQueryClient();
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);

  return useMutation({
    mutationFn: ({ evaluationId, findingIndex, text }: SubmitEvaluationFeedbackParams) => {
      if (!selectedOrganizationId) {
        throw new Error('Missing organization context');
      }

      const trimmedText = text.trim();
      if (!trimmedText) {
        throw new Error('Feedback text is required');
      }

      const request: SubmitFeedbackRequest = {
        organization_id: selectedOrganizationId,
        feedback_type:
          findingIndex === undefined
            ? FeedbackType.FEEDBACK_TYPE_EVALUATION
            : FeedbackType.FEEDBACK_TYPE_EVALUATION_FINDING,
        evaluation_id: evaluationId,
        text: trimmedText,
        ...(findingIndex === undefined ? {} : { finding_index: findingIndex }),
      };

      return feedbackService.submitFeedback(request);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.feedback.byEvaluation(variables.evaluationId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.feedback.all,
      });
      toast.success('Feedback submitted');
    },
    onError: (_error: AxiosError) => {
      toast.error('Failed to submit feedback');
    },
  });
};

interface UpdateEvaluationFeedbackParams {
  evaluationId: string;
  feedbackId: string;
  text: string;
}

export const useUpdateEvaluationFeedback = () => {
  const queryClient = useQueryClient();
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);

  return useMutation({
    mutationFn: ({ feedbackId, text }: UpdateEvaluationFeedbackParams) => {
      if (!selectedOrganizationId) {
        throw new Error('Missing organization context');
      }

      const trimmedText = text.trim();
      if (!trimmedText) {
        throw new Error('Feedback text is required');
      }

      return feedbackService.updateFeedback({
        organization_id: selectedOrganizationId,
        feedback_id: feedbackId,
        text: trimmedText,
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.feedback.byEvaluation(variables.evaluationId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.feedback.all,
      });
      toast.success('Feedback updated');
    },
    onError: (_error: AxiosError) => {
      toast.error('Failed to update feedback');
    },
  });
};
